import { useEffect } from 'react';
import type { RumEvent } from '@datadog/browser-rum';
import { datadogRum } from '@datadog/browser-rum';

/** Transcend's environment names in DataDog */
type DataDogEnvironment = 'prod' | 'staging' | 'dev';

/**
 * Noisy browser/runtime errors that are not actionable in RUM alerting.
 */
const IGNORED_ERROR_RULES: {
  messages: string[];
  contexts?: string[];
}[] = [
  {
    messages: [
      "Failed to execute 'insertBefore' on 'Node'",
      "Failed to execute 'removeChild' on 'Node'",
      'Missing required parameters: sitekey',
      'Uncaught "Script error."',
      'Cancel rendering route',
    ],
  },
  {
    messages: ['Cannot read properties of undefined'],
    contexts: ['recaptcha__en.js', '/recaptcha/'],
  },
  {
    messages: ['signal is aborted without reason'],
    contexts: ['cdn-0.d41.co/tags/dnb_coretag'],
  },
  {
    messages: [
      'Failed to load https://www.googletagmanager.com/gtag/js',
      'Failed to create PluginFactory',
    ],
    contexts: [
      'cdn.segment.com/next-integrations/actions',
      'google-analytics-4-web',
    ],
  },
];

function shouldIgnoreRumError(event: RumEvent): boolean {
  if (event.type !== 'error') {
    return false;
  }

  const message = event.error?.message ?? '';
  const stack = event.error?.stack ?? '';

  return IGNORED_ERROR_RULES.some(
    ({ messages, contexts }) =>
      messages.some((pattern) => message.includes(pattern)) &&
      (!contexts ||
        contexts.some(
          (pattern) => message.includes(pattern) || stack.includes(pattern),
        )),
  );
}

const VERCEL_ENV_TO_DATADOG_ENV: Record<string, DataDogEnvironment> = {
  production: 'prod',
  preview: 'staging',
  development: 'dev',
};

const ENV_TO_LOAD: DataDogEnvironment[] = ['prod', 'staging'];

/**
 * Hook to initialize DataDog RUM and return the object
 */
export function useDataDogRum({
  draftMode,
}: {
  draftMode: boolean;
}): typeof datadogRum {
  useEffect(() => {
    // Set draftMode on the global context, to solve for cases where the below draftMode is still on after DD has initialized
    datadogRum.setGlobalContextProperty('draftMode', draftMode);

    // If draftMode, don't init
    if (draftMode) {
      return;
    }

    // If DD_RUM is already on window and it's configured, don't re-init
    if (
      'DD_RUM' in window &&
      !!(window.DD_RUM as typeof datadogRum).getInitConfiguration()
    ) {
      return;
    }

    // Get the DataDog env
    const env =
      VERCEL_ENV_TO_DATADOG_ENV[
        process.env.NEXT_PUBLIC_VERCEL_ENV || 'development'
      ];

    // Only load if we're in Vercel `preview` or `production`
    if (!ENV_TO_LOAD.includes(env)) {
      return;
    }

    // Variables for the config object
    const gitShortSHA =
      process.env.NEXT_PUBLIC_VERCEL_GIT_COMMIT_SHA?.slice(0, 7) || 'localhost';
    const hostname = process.env.NEXT_PUBLIC_VERCEL_URL || `localhost:3000`;

    datadogRum.init({
      applicationId: '8ddb2f2b-50dd-46b2-aa11-128cda313db8',
      clientToken: 'pub52c3744eb4b95eb08e286fd21f06196a',
      site: 'datadoghq.com',
      service: 'website-2024',
      env,
      version: gitShortSHA,
      // 1% keeps Investigate-tier RUM cost ~99% lower than full sampling on
      // the public marketing site while still catching broad error trends.
      sessionSampleRate: 1,
      sessionReplaySampleRate: 0,
      trackUserInteractions: true,
      trackResources: true,
      trackLongTasks: true,
      defaultPrivacyLevel: 'mask-user-input',
      sessionPersistence: 'local-storage',
      allowedTracingUrls: [
        {
          match: `https://${hostname}/api/`,
          propagatorTypes: ['tracecontext'],
        },
        {
          match: 'https://docs.transcend.io/api/',
          propagatorTypes: ['tracecontext'],
        },
      ],
      beforeSend: (event: RumEvent) => {
        if (shouldIgnoreRumError(event)) {
          return false;
        }
        return true;
      },
    });
  }, [draftMode]);

  return datadogRum;
}
